import React from 'react';
import ReactDOM from 'react-dom';
import { Button, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClipboardList, faSpinner, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    CollapseUnderlineHeader,
    toasty,
} from '../common/forms/FormElements';
import SlideForm from '../common/forms/SlideForm';
import CommonContext, { ApiRoutes, CustomFormContext, isValidGuid } from '../Common';
import CustomFormComponent, { resolveFieldIsRequired } from './CustomFormComponent';
import './CustomFormSlideout.scss';

const fetchForJob = (jobId, companyId, sessionId, formContextId = CustomFormContext.Job, contractId = null) => httpClient.post(ApiRoutes.customForm.forContext(), {
    companyId, formContextId, entityId: jobId || null, sessionId, contractId,
});

const fetchForTimesheets = (timesheetIds, formContextId) => httpClient.js(
    ApiRoutes.customForm.forTimesheets(timesheetIds, formContextId),
);

const fetchForQuickSale = (quickSaleId, sessionId, companyId) => httpClient.post(ApiRoutes.customForm.forContext(), {
    companyId, formContextId: CustomFormContext.QuickSale, entityId: quickSaleId || null, sessionId,
});

const isFormInstanceCompleted = (fi) => {
    const requiredDetails = (fi.details ?? []).filter((d) => d.customFormField?.isRequiredToComplete);
    return requiredDetails.every((d) => {
        const fieldType = d.customFormField?.fieldType ?? d.fieldType;
        return fieldType === 'attachment' ? !!d.attachmentId : !!d.value;
    });
};

const isFormRequired = (fi, isEmergency) => {
    const emergency = isEmergency ?? fi.isEmergency;
    return emergency ? fi.customForm?.isRequiredOnEmergency : fi.customForm?.isRequired;
};

const computeAllCompleted = (formInstances, isEmergency) => {
    if (!formInstances?.length) return false;
    const applicable = formInstances.filter((fi) => isFormRequired(fi, isEmergency));
    return applicable.length === 0 || applicable.every(isFormInstanceCompleted);
};

export default class CustomForms extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            show: false,
            buttonLoading: false,
            completed: props.defaultCompleted ?? true,
            loading: false,
            saving: false,
            formInstances: [],
            formRefs: {},
            collapsedForms: {},
            formValidated: false,
        };
    }

    componentDidMount() {
        this.loadForms();
    }

    componentDidUpdate(prevProps) {
        const loadKeys = ['jobId', 'companyId', 'contractId', 'sessionId', 'formContextId', 'quickSaleId', 'selectedFormContextId'];
        if (loadKeys.some((k) => prevProps[k] !== this.props[k])) {
            this.loadForms();
        }
        if (prevProps.isEmergency !== this.props.isEmergency) {
            const { formInstances } = this.state;
            const allCompleted = computeAllCompleted(formInstances, this.props.isEmergency);
            this.setState({ completed: allCompleted }, () => {
                this.props.onCompletionChange?.(allCompleted);
            });
        }
    }

    fetchForms = async () => {
        const {
            jobId,
            companyId,
            contractId,
            sessionId,
            formContextId,
            invoice,
            quickSaleId,
            selectedFormContextId,
        } = this.props;

        if (formContextId === CustomFormContext.Invoice && !selectedFormContextId) {
            return { formInstances: [], formRefs: {} };
        }

        let formInstances;
        if (formContextId === CustomFormContext.Invoice && invoice) {
            const timesheetIds = (invoice.invoiceTimesheets ?? []).map((t) => t.timesheetId);
            formInstances = await fetchForTimesheets(timesheetIds, selectedFormContextId);
        } else if (formContextId === CustomFormContext.QuickSale) {
            formInstances = await fetchForQuickSale(quickSaleId, sessionId, companyId, formContextId);
        } else {
            formInstances = await fetchForJob(jobId, companyId, sessionId, formContextId, contractId);
        }

        const { formRefs: existingRefs } = this.state;
        const formRefs = (formInstances ?? []).reduce((acc, fi) => {
            const key = `${fi.customFormId}-${fi.jobId}-${fi.quickSaleId}`;
            acc[key] = existingRefs[key] ?? React.createRef();
            return acc;
        }, {});

        return { formInstances: formInstances ?? [], formRefs };
    };

    open = async () => {
        this.props.onOpen?.();
        this.context.setFormOpened(true);
        this.setState({ show: true, loading: true, formValidated: false });
        try {
            const { formInstances, formRefs } = await this.fetchForms();
            this.setState({ formInstances, formRefs, loading: false });
        } catch {
            toasty.error('Failed to load forms.');
            this.setState({ loading: false });
        }
    };

    loadForms = async () => {
        this.setState({ buttonLoading: true });
        try {
            const { formInstances, formRefs } = await this.fetchForms();
            const allCompleted = computeAllCompleted(formInstances, this.props.isEmergency);
            this.setState({ formInstances, formRefs, completed: allCompleted }, () => {
                this.props.onCompletionChange?.(allCompleted);
            });
        } catch {
        } finally {
            this.setState({ buttonLoading: false });
        }
    };



    resolveIsRequired = (field, detail, siblingDetails = []) =>
        resolveFieldIsRequired(field, detail, siblingDetails, this.props.controlValues);

    validateRequiredFields = ({ checkUnsaved = true } = {}) => {
        const { formInstances, formRefs } = this.state;
        const { isEmergency } = this.props;
        const applicableForms = formInstances.filter((fi) => isFormRequired(fi, isEmergency));

        const missing = [];
        let hasUnsaved = false;
        for (const fi of applicableForms) {
            const key = `${fi.customFormId}-${fi.jobId}-${fi.quickSaleId}`;
            const ref = formRefs[key];
            const details = ref?.current
                ? ref.current.getFormData().details
                : (fi.details ?? []);

            if (checkUnsaved && ref?.current?.isDirty?.()) {
                hasUnsaved = true;
            }

            for (const d of details) {
                const field = d.customFormField ?? {};
                const isRequired = this.resolveIsRequired(field, d, details);
                if (!isRequired) continue;

                const fieldType = field.fieldType ?? d.fieldType;
                const hasValue = fieldType === 'attachment'
                    ? !!d.attachmentId
                    : !!d.value;

                if (!hasValue) {
                    const err = `Missing "${field.label ?? d.fieldLabel ?? 'Unknown field'}"`;
                    missing.push(err);
                }
            }
        }

        if (hasUnsaved) {
            missing.unshift('Form has unsaved changes');
        }

        return missing;
    };

    validate = () => {
        const missing = this.validateRequiredFields();
        if (missing.length > 0) {
            const { validationMessage } = this.props;
            toasty.error(validationMessage ?? 'Complete and Save the Form.', missing.join(', '));
            this.open();
            return false;
        }
        return true;
    };

    onClose = () => {
        this.context.setFormOpened(false);
        this.setState({ show: false });
        this.props.onClose?.();
    };

    onSubmit = async () => {
        const {
            formInstances, formRefs, saving,
        } = this.state;
        const {
            sessionId, jobId, formContextId, selectedFormContextId, quickSaleId, onSave,
        } = this.props;
        if (saving) return;

        const missing = this.validateRequiredFields({ checkUnsaved: false });
        if (missing.length > 0) {
            toasty.error('Please complete all required fields.', missing.join(', '));
            return;
        }

        this.setState({ saving: true });
        try {
            const saves = formInstances
                .map((fi) => {
                    const ref = formRefs[`${fi.customFormId}-${fi.jobId}-${fi.quickSaleId}`];
                    if (!ref?.current) return null;
                    const { id, details } = ref.current.getFormData();
                    if (isValidGuid(id)) {
                        return httpClient.put(
                            ApiRoutes.customForm.update(id),
                            details,
                        );
                    }

                    const hasData = details.some((d) => d.value || d.attachmentId);

                    if (!hasData) return null;

                    const body = {
                        customFormId: fi.customFormId,
                        formContextId,
                        details,
                    };

                    if (formContextId == CustomFormContext.Job) {
                        if (sessionId && !jobId) {
                            body.sessionId = sessionId;
                        } else if (jobId) {
                            body.jobId = jobId;
                        }
                    }

                    if (formContextId == CustomFormContext.QuickSale) {
                        if (sessionId && !quickSaleId) {
                            body.sessionId = sessionId;
                        } else if (quickSaleId) {
                            body.quickSaleId = quickSaleId;
                        }
                    }

                    //create new forms from invoice
                    if (formContextId == CustomFormContext.Invoice && selectedFormContextId == CustomFormContext.Job) {
                        body.jobId = fi.jobId;
                        body.formContextId = selectedFormContextId;
                    }

                    if (formContextId == CustomFormContext.Invoice && selectedFormContextId == CustomFormContext.QuickSale) {
                        body.quickSaleId = fi.quickSaleId;
                        body.formContextId = selectedFormContextId;
                    }

                    return httpClient.post(
                        ApiRoutes.customForm.create(),
                        body,
                    );
                })
                .filter(Boolean);

            await Promise.all(saves);
            toasty.success('Form Saved.');
            if (onSave) {
                onSave();
            }
            await this.loadForms();
            this.onClose();
        } catch {
            toasty.error('Failed to save form.');
        } finally {
            this.setState({ saving: false });
        }
    };

    deleteFormInstance = async (formInstanceId) => {
        await httpClient.delete(ApiRoutes.customForm.delete(formInstanceId));

        const { formInstances: refreshed, formRefs: refreshedRefs } = await this.fetchForms();

        const allCompleted = computeAllCompleted(refreshed);
        this.setState({ formInstances: refreshed, formRefs: refreshedRefs, completed: allCompleted }, () => {
            this.props.onCompletionChange?.(allCompleted);
        });

        toasty.success('Form deleted.');
    };

    toggleForm = (customFormId) => {
        this.setState((prev) => ({
            collapsedForms: {
                ...prev.collapsedForms,
                [customFormId]: !prev.collapsedForms[customFormId],
            },
        }));
    };

    render() {
        const {
            show, buttonLoading, completed, loading, saving,
            formInstances, formRefs, collapsedForms, formValidated,
        } = this.state;
        const {
            label = 'Forms',
            className = '',
            isEmergency,
            showButton = true,
            disabled,
            tooltipId,
            readOnly,
            selectedFormContextId,
            formContextId,
            controlValues,
        } = this.props;

        const hasRequiredForms = formInstances.some((fi) => isFormRequired(fi, isEmergency));
        const formContext = selectedFormContextId ?? formContextId;
        const formLabel = `${Object.keys(CustomFormContext).find(
            (k) => CustomFormContext[k] === formContext,
        )} Forms`;

        return (
            <>
                {showButton && (
                    <Button
                        color="link"
                        className={`site-link no-ellipsis${className ? ` ${className}` : ''}`}
                        disabled={disabled}
                        onClick={this.open}
                    >
                        <FontAwesomeIcon
                            icon={faClipboardList}
                            className="mr-1"
                        />
                        {label}
                        {buttonLoading && (
                            <FontAwesomeIcon
                                icon={faSpinner}
                                spin
                                className="ml-1"
                            />
                        )}
                        {/* {!buttonLoading && completed && (
                            <FontAwesomeIcon
                                icon={faCheck}
                                className="ml-1"
                            />
                        )} */}
                        {!buttonLoading && !completed && hasRequiredForms && tooltipId && (
                            <>
                                <FontAwesomeIcon
                                    id={tooltipId}
                                    icon={faExclamationTriangle}
                                    className="ml-1 text-danger"
                                />
                                <UncontrolledTooltip target={tooltipId}>
                                    One or more required forms are incomplete
                                </UncontrolledTooltip>
                            </>
                        )}
                        {!buttonLoading && !completed && hasRequiredForms && !tooltipId && (
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="ml-1 text-danger"
                            />
                        )}
                    </Button>
                )}
                {show && ReactDOM.createPortal(
                    <div onSubmit={(e) => e.stopPropagation()}>
                        <SlideForm
                            show={show}
                            onClose={this.onClose}
                            onSubmit={this.onSubmit}
                            formIcon={faClipboardList}
                            formTitle={formLabel}
                            loading={loading}
                            saving={saving}
                            saveDisabled={loading || saving}
                            readOnly={readOnly}
                            setIsValidated={(value) => {
                                this.setState({ formValidated: value });
                            }}
                            isValidated={formValidated}
                            id="customFormSlideout"
                            className="custom-form-slideout"
                        >
                            {formInstances.map((fi) => (
                                <CollapseUnderlineHeader
                                    key={`${fi.id}`}
                                    headerText={fi.customFormName ?? 'Custom Form'}
                                    isOpen={collapsedForms[fi.customFormId] !== false}
                                    toggleCollapse={() => this.toggleForm(fi.customFormId)}
                                >
                                    <CustomFormComponent
                                        ref={formRefs[`${fi.customFormId}-${fi.jobId}-${fi.quickSaleId}`]}
                                        formInstance={fi}
                                        onDeleteFormInstance={this.deleteFormInstance}
                                        readOnly={readOnly}
                                        controlValues={controlValues}
                                        formValidated={formValidated}
                                    />
                                </CollapseUnderlineHeader>
                            ))}

                            {!loading && formInstances.length === 0 && (
                                <p className="text-muted text-center mt-3">
                                    No forms configured.
                                </p>
                            )}
                        </SlideForm>
                    </div>,
                    document.body,
                )}
            </>
        );
    }
}
